import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { PHONE_DISPLAY, PHONE_HREF } from './assets';
import { getAttribution } from '../../utils/attribution';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module 8 Section 5 — verbatim consent language. Displayed exactly as shown and
// submitted to the server for evidence. NEVER edit without bumping the backend version.
export const SMS_CONSENT_TEXT = "I agree to receive text messages from Gleam Guru Cleaners regarding my quote request, appointment scheduling, appointment reminders, invoices, account updates, and customer support. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.";
export const EMAIL_CONSENT_TEXT = "I'd like to receive the Gleam Guru email newsletter, seasonal cleaning tips, exclusive promotions, company updates, and special offers. I understand I can unsubscribe at any time.";

const SERVICE_LABELS = { standard_home: 'Standard Home Clean', deep_reset: 'Signature Deep Reset™', airbnb: 'Airbnb Turnover', commercial: 'Commercial Cleaning', move_in_move_out: 'Move-In / Move-Out Cleaning' };
const SERVICES = [
  { value: 'standard_home', label: 'Standard Home Clean', sub: 'Routine upkeep' },
  { value: 'deep_reset', label: 'Signature Deep Reset™', sub: 'Top-to-bottom' },
  { value: 'airbnb', label: 'Airbnb Turnover', sub: 'Between guests' },
  { value: 'commercial', label: 'Commercial', sub: 'Office & retail' },
  { value: 'move_in_move_out', label: 'Move-In / Move-Out', sub: 'Residential transitions' },
];
const MOVE_SERVICE = 'move_in_move_out';
const DIRECTIONS = [['move_in', 'Move-In'], ['move_out', 'Move-Out']];
const PROPERTIES = [['home', 'Home'], ['apartment', 'Apartment / Condo'], ['office', 'Office'], ['retail', 'Retail']];
const SOURCES = [
  ['TaskRabbit', 'TaskRabbit'], ['Google Search', 'Google Search'], ['Google Business Profile', 'Google Business'],
  ['Yelp', 'Yelp'], ['Instagram', 'Instagram'], ['Facebook', 'Facebook'], ['Nextdoor', 'Nextdoor'], ['Referral', 'Referral'],
  ['Repeat Client', 'Repeat Client'], ['Saw Our Vehicle / Sign', 'Vehicle / Sign'], ['Flyer / Door Hanger', 'Flyer / Door Hanger'], ['Other', 'Other'],
];
const STAGES = ['Service', 'Property Type', 'Contact', 'Source', 'Details'];
const CONTACT_STAGE = 2;
const LAST = 4;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneValid = (v) => { const d = v.replace(/\D/g, ''); return d.length >= 10 && d.length <= 15 && /^[+()\d\s.-]+$/.test(v.trim()); };

const slug = (v) => v.replace(/\W+/g, '-').toLowerCase();
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const bounce = (el) => {
  el.classList.remove('bounce');
  void el.offsetWidth;
  el.classList.add('bounce');
  el.addEventListener('animationend', () => el.classList.remove('bounce'), { once: true });
};

const Choice = ({ selected, onSelect, order, testId, className = 'choice', children }) => (
  <button
    type="button"
    className={className}
    aria-pressed={selected ? 'true' : 'false'}
    style={{ '--order': order }}
    onClick={(e) => { onSelect(); bounce(e.currentTarget); }}
    data-testid={testId}
  >
    {children}
  </button>
);

const Field = ({ label, wide, children }) => (
  <label className={wide ? 'wide' : undefined}>{label}{children}</label>
);

export const LeadGenerator = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requested = searchParams.get('service') || '';
  const preselected = SERVICE_LABELS[requested] ? requested : '';

  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const timerRef = useRef(null);
  const submissionIdRef = useRef(null);
  if (!submissionIdRef.current) {
    submissionIdRef.current = window.crypto?.randomUUID?.() || `sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const [form, setForm] = useState({
    full_name: '', phone: '', email: '', service_needed: preselected, move_direction: '',
    property_type: '', additional_details: '', lead_source: '',
  });
  const [stage, setStage] = useState(0);
  const [anim, setAnim] = useState('');
  const [flash, setFlash] = useState(false);
  const [flowError, setFlowError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const transitioning = useRef(false);
  const isMove = form.service_needed === MOVE_SERVICE;

  const update = (field, value) => { setForm((p) => ({ ...p, [field]: value })); setFlowError(''); };
  const chooseService = (v) => { setForm((p) => ({ ...p, service_needed: v, move_direction: v === MOVE_SERVICE ? p.move_direction : '' })); setFlowError(''); };

  // Smooth scroll when arriving via /?service=...#quote-form or /#quote-form
  useEffect(() => {
    if (!(preselected || location.hash === '#quote-form')) return;
    const t = setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (preselected) triggerFlash();
    }, 250);
    return () => clearTimeout(t);
  }, [preselected, location.key, location.hash]);

  useEffect(() => { if (preselected) chooseService(preselected); }, [preselected]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const triggerFlash = () => {
    setFlash(false);
    requestAnimationFrame(() => setFlash(true));
  };

  // Error for a stage that must be complete before moving past it ('' when complete).
  const stageError = (i) => {
    if (i === 0 && !form.service_needed) return 'Please choose your service.';
    if (i === 0 && isMove && !form.move_direction) return 'Please choose Move-In Cleaning or Move-Out Cleaning.';
    if (i === 1 && !form.property_type) return 'Please choose your property type.';
    if (i === CONTACT_STAGE) {
      if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim()) return 'Please complete your name, phone number, and email before continuing.';
      if (!phoneValid(form.phone)) return 'Please enter a valid phone number, including area code.';
      if (!EMAIL_RE.test(form.email.trim())) return 'Please enter a valid email address.';
    }
    return '';
  };
  const firstBlocked = (upTo) => { for (let i = 0; i < upTo; i += 1) if (stageError(i)) return i; return -1; };

  const showStage = (target) => {
    if (transitioning.current || target === stage) return;
    let n = target;
    const blocked = n > stage ? firstBlocked(n) : -1;
    if (blocked >= 0) {
      if (blocked === stage) { setFlowError(stageError(blocked)); return; }
      n = blocked;
    }
    const message = blocked >= 0 ? stageError(blocked) : '';
    transitioning.current = true;
    const dir = n > stage ? 'forward' : 'back';
    setAnim(`exit-${dir}`);
    timerRef.current = setTimeout(() => {
      setStage(n);
      setFlowError(message);
      setAnim(`enter-${dir}`);
      triggerFlash();
      const el = formRef.current;
      if (el && document.documentElement.clientWidth < 550 && el.getBoundingClientRect().top < 0) {
        el.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
      }
      requestAnimationFrame(() => el?.querySelector('.stage h2')?.focus({ preventScroll: true }));
      transitioning.current = false;
    }, reducedMotion() ? 0 : 180);
  };

  const handleContinue = () => {
    if (stage < LAST) { showStage(stage + 1); return; }
    const blocked = firstBlocked(LAST);
    if (blocked >= 0) { showStage(blocked); return; }
    submit();
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setFlowError('');
    try {
      await axios.post(`${API_URL}/api/quote-request`, {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        service_needed: form.service_needed,
        move_direction: isMove ? form.move_direction : null,
        property_type: form.property_type,
        additional_details: form.additional_details.trim(),
        lead_source: form.lead_source || null,
        attribution: getAttribution(),
        source_page: 'homepage',
        client_submission_id: submissionIdRef.current,
      });
      toast.success('Quote request submitted!');
      navigate('/quote-received', {
        state: {
          confirmed: true,
          submission_id: submissionIdRef.current,
          first_name: form.full_name.trim().split(/\s+/)[0] || '',
          service: SERVICE_LABELS[form.service_needed] || '',
        },
      });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : 'We couldn’t send your request. Please try again.';
      setFlowError(`${msg} Your answers are still here.`);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const percent = Math.round((stage / 5) * 100);
  const stageClass = `stage ${anim}`;

  return (
    <section className="lead" id="quote-form" aria-label="Tell us about your space" ref={sectionRef} data-testid="lead-form-section">
      <div>
        <p className="lead-label">FREE ESTIMATE · NO OBLIGATION</p>
        <h1>Tell Us About<span>Your Space.</span></h1>
        <div className="progress">
          <span>YOUR PROGRESS</span>
          <span className="track"><div className="track-fill" style={{ width: `${percent}%` }} /></span>
          <span data-testid="lead-progress-percent">{percent}%</span>
        </div>
        <nav className="flow-nav" aria-label="Lead capture stages">
          {STAGES.map((name, i) => (
            <button
              key={name}
              type="button"
              className={i === stage ? 'current' : ''}
              aria-current={i === stage ? 'step' : 'false'}
              onClick={() => showStage(i)}
              data-testid={`lead-nav-${slug(name)}`}
            >
              <span>{`0${i + 1}`}</span>{name}
            </button>
          ))}
        </nav>
        <a className="call-link" href={PHONE_HREF} data-testid="lead-call-link">Prefer to talk? {PHONE_DISPLAY}</a>
      </div>

      <form
        className={`lead-form ${flash ? 'change-flash' : ''}`}
        ref={formRef}
        noValidate
        onSubmit={(e) => { e.preventDefault(); handleContinue(); }}
        onAnimationEnd={(e) => { if (e.target === formRef.current) setFlash(false); }}
        data-testid="quote-request-form"
      >
        {stage === 0 && (
          <div className={stageClass} data-stage="0">
            <div className="step-heading"><span className="step">01</span><h2 tabIndex={-1}>What do you need cleaned?</h2></div>
            <p className="stage-description">Select the service that fits your needs.</p>
            <div className="services" data-testid="form-service-needed">
              {SERVICES.map((s, i) => (
                <Choice key={s.value} className="service" order={i} selected={form.service_needed === s.value} onSelect={() => chooseService(s.value)} testId={`svc-${s.value}`}>
                  <strong>{s.label}</strong><small>{s.sub}</small><span className="radio" />
                </Choice>
              ))}
            </div>
            {isMove && (
              <div className="move-specific move-direction-field" data-testid="move-direction-block">
                <p className="field-caption">MOVE-IN OR MOVE-OUT?</p>
                <div className="choices" data-group="move_direction">
                  {DIRECTIONS.map(([v, label], i) => (
                    <Choice key={v} order={i} selected={form.move_direction === v} onSelect={() => update('move_direction', v)} testId={`move-direction-${v}`}>{label}</Choice>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {stage === 1 && (
          <div className={stageClass} data-stage="1">
            <div className="step-heading"><span className="step">02</span><h2 tabIndex={-1}>What type of property?</h2></div>
            <p className="stage-description">So we send the right team &amp; supplies.</p>
            <div className="choices" data-group="property" data-testid="form-property-type">
              {PROPERTIES.map(([v, label], i) => (
                <Choice key={v} order={i} selected={form.property_type === v} onSelect={() => update('property_type', v)} testId={`prop-${v}`}>{label}</Choice>
              ))}
            </div>
          </div>
        )}

        {stage === 2 && (
          <div className={stageClass} data-stage="2">
            <div className="step-heading"><span className="step">03</span><h2 tabIndex={-1}>How can we reach you?</h2></div>
            <p className="stage-description">We’ll only contact you about your cleaning request. All three fields are required.</p>
            <div className="fields">
              <Field label="FULL NAME *" wide>
                <input name="full-name" autoComplete="name" required maxLength={120} value={form.full_name} onChange={(e) => update('full_name', e.target.value)} data-testid="form-full-name" />
              </Field>
              <Field label="PHONE *">
                <input name="phone" type="tel" autoComplete="tel" inputMode="tel" required maxLength={30} value={form.phone} onChange={(e) => update('phone', e.target.value)} data-testid="form-phone" />
              </Field>
              <Field label="EMAIL *">
                <input name="email" type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="form-email" />
              </Field>
            </div>
          </div>
        )}

        {stage === 3 && (
          <div className={stageClass} data-stage="3">
            <div className="step-heading"><span className="step">04</span><h2 tabIndex={-1}>How did you hear about us?</h2></div>
            <p className="stage-description">Optional — helps us know what's working.</p>
            <div className="choices" data-group="referral" data-testid="form-lead-source">
              {SOURCES.map(([v, label], i) => (
                <Choice key={v} order={i} selected={form.lead_source === v} onSelect={() => update('lead_source', form.lead_source === v ? '' : v)} testId={`lead-source-${slug(v)}`}>{label}</Choice>
              ))}
            </div>
          </div>
        )}

        {stage === 4 && (
          <div className={stageClass} data-stage="4">
            <div className="step-heading"><span className="step">05</span><h2 tabIndex={-1}>Anything you’d like us to know?</h2></div>
            <p className="stage-description">Optional. Leave a quick note, or send your request now. We’ll work through the cleaning details with you next.</p>
            <div className="fields" style={{ marginTop: 24 }}>
              <Field label="ANYTHING ELSE WE SHOULD KNOW? (OPTIONAL)" wide>
                <textarea name="details" maxLength={2000} value={form.additional_details} onChange={(e) => update('additional_details', e.target.value)} data-testid="form-details" />
              </Field>
            </div>
            <div className="policy-links" style={{ marginTop: 18 }} data-testid="compliance-links">
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="link-privacy-policy">Privacy Policy</Link>
            </div>
          </div>
        )}

        {flowError && <p className="flow-error" role="alert" data-testid="lead-flow-error">{flowError}</p>}
        <div className="form-actions">
          <button className="back" type="button" hidden={stage === 0} onClick={() => showStage(Math.max(0, stage - 1))} data-testid="lead-back-btn">← Back</button>
          <span className="stage-count" data-testid="lead-stage-count">{`0${stage + 1} / 05`}</span>
          <button className="continue" type="submit" disabled={submitting} data-testid={stage === LAST ? 'form-submit-btn' : 'lead-continue-btn'}>
            <span>{submitting ? 'SUBMITTING…' : stage === LAST ? 'TALK WITH A GLEAM GURU' : 'CONTINUE'}</span>
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </form>
    </section>
  );
};
